import React, { useState, useRef, useEffect } from 'react';
import { showSuccess, showError, showWarning } from '../utils/alertas';

// Ajuste a URL conforme o ambiente
const IS_PROD = true; // Mude para true em produção
const API_BASE_URL = IS_PROD ? 'https://sigoapi.jhe-etes.com.br/api' : 'http://localhost:3001/api';

function FluxogramaModal({ isOpen, onClose, empreendimentoId, onSuccess, currentData }) {
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [existingImage, setExistingImage] = useState(null);
  const [existingVideo, setExistingVideo] = useState(null);
  const [descricao, setDescricao] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
        setExistingImage(currentData?.fluxograma_url || null);
        setExistingVideo(currentData?.fluxograma_video_url || null);
        setSelectedImage(null);
        setSelectedVideo(null);
        setDescricao('');
    }
  }, [isOpen, currentData]);

  // 1. UPLOAD DE ARQUIVO (Com Token)
  const uploadFile = async (file) => {
      const token = localStorage.getItem('token'); // 🔐 Pega o Token

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_BASE_URL}/upload`, { 
          method: 'POST', 
          headers: {
            'Authorization': `Bearer ${token}` // 🔐 Envia no Header (Sem Content-Type pois é FormData)
          },
          body: formData 
      });

      if (!response.ok) throw new Error(`Falha no upload: ${file.name}`);
      const data = await response.json();
      return data.url; 
  };

  const handleSave = async () => {
    // Validação Visual
    if (!descricao.trim()) {
        showWarning("Campo Obrigatório", "Por favor, digite o motivo desta atualização para o histórico.");
        return;
    }

    if (!selectedImage && !selectedVideo) {
        showWarning("Nenhum arquivo", "Você não selecionou nenhum arquivo novo para atualizar.");
        return;
    }

    setLoading(true);
    
    // 🔐 Pega o Token para a atualização
    const token = localStorage.getItem('token'); 

    try {
        let finalImageUrl = existingImage; 
        let finalVideoUrl = existingVideo;
        let upouImagem = false;
        let upouVideo = false;

        // Faz os uploads se houver arquivos novos
        if (selectedImage) {
            finalImageUrl = await uploadFile(selectedImage);
            upouImagem = true; 
        }

        if (selectedVideo) {
            finalVideoUrl = await uploadFile(selectedVideo);
            upouVideo = true; 
        }

        const payload = {
            fluxograma_url: finalImageUrl,       
            fluxograma_video_url: finalVideoUrl, 
            fluxograma_descricao: descricao,
            upou_imagem: upouImagem,
            upou_video: upouVideo,
            usuario_responsavel: "Admin" // O backend vai pegar o nome real pelo Token, isso fica de fallback
        };

        // 2. SALVAR DADOS (Com Token)
        const updateRes = await fetch(`${API_BASE_URL}/empreendimentos/${empreendimentoId}`, {
            method: 'PUT',
            headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}` // 🔐 Envia no Header
            },
            body: JSON.stringify(payload)
        });

        if (updateRes.ok) {
            await showSuccess("Atualizado!", "Os arquivos foram enviados com sucesso.");
            onSuccess();
            onClose();
        } else {
            const errData = await updateRes.json();
            showError("Erro ao Salvar", errData.message || "Ocorreu um erro desconhecido.");
        }

    } catch (error) {
        console.error(error);
        showError("Erro de Conexão", error.message);
    } finally {
        setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="bg-slate-50 px-8 py-5 border-b border-gray-100 flex justify-between items-center shrink-0">
          <h2 className="text-lg font-bold text-gray-800">📐 Atualizar Arquivos do Processo</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-red-500 text-2xl leading-none">×</button>
        </div>

        <div className="p-8 overflow-y-auto custom-scrollbar space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Imagem */}
                <div className="space-y-2">
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">Fluxograma (Imagem/PDF)</label>
                    <div onClick={() => imageInputRef.current.click()} className={`border-2 border-dashed rounded-2xl h-40 flex flex-col items-center justify-center cursor-pointer transition-all group relative overflow-hidden ${selectedImage ? 'border-sabesp-cyan bg-cyan-50' : 'border-gray-300 hover:border-sabesp-cyan hover:bg-gray-50'}`}>
                        <input type="file" ref={imageInputRef} onChange={(e) => e.target.files[0] && setSelectedImage(e.target.files[0])} className="hidden" accept="image/*,.pdf" />
                        {selectedImage ? (
                            <div className="text-center p-2"><span className="text-2xl mb-1 block">✅</span><p className="text-sabesp-cyan font-bold text-xs truncate max-w-[150px]">{selectedImage.name}</p><span className="text-[10px] text-gray-500">Clique para trocar</span></div>
                        ) : (
                            <div className="text-center p-2"><span className="text-3xl mb-2 block opacity-30">🖼️</span>{existingImage ? (<><p className="text-green-600 font-bold text-xs">Já existe um arquivo.</p><p className="text-[10px] text-gray-400">Clique para substituir</p></>) : (<p className="text-xs text-gray-400 font-bold">Clique para adicionar</p>)}</div>
                        )}
                    </div>
                </div>
                {/* Vídeo */}
                <div className="space-y-2">
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider ml-1">Vídeo Explicativo (MP4)</label>
                    <div onClick={() => videoInputRef.current.click()} className={`border-2 border-dashed rounded-2xl h-40 flex flex-col items-center justify-center cursor-pointer transition-all group relative overflow-hidden ${selectedVideo ? 'border-purple-500 bg-purple-50' : 'border-gray-300 hover:border-purple-500 hover:bg-purple-50'}`}>
                        <input type="file" ref={videoInputRef} onChange={(e) => e.target.files[0] && setSelectedVideo(e.target.files[0])} className="hidden" accept="video/mp4,video/quicktime,video/webm" />
                        {selectedVideo ? (
                            <div className="text-center p-2"><span className="text-2xl mb-1 block">🎥</span><p className="text-purple-600 font-bold text-xs truncate max-w-[150px]">{selectedVideo.name}</p><span className="text-[10px] text-gray-500">Clique para trocar</span></div>
                        ) : (
                            <div className="text-center p-2"><span className="text-3xl mb-2 block opacity-30">🎬</span>{existingVideo ? (<><p className="text-purple-600 font-bold text-xs">Já existe um vídeo.</p><p className="text-[10px] text-gray-400">Clique para substituir</p></>) : (<p className="text-xs text-gray-400 font-bold">Clique para adicionar</p>)}</div>
                        )}
                    </div>
                </div>
            </div>
            <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 ml-1">Motivo da Atualização (Obrigatório) *</label>
                <textarea value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="Ex: Atualização do desenho técnico e inclusão de vídeo da fase 2..." className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-sabesp-cyan focus:ring-2 focus:ring-cyan-100 outline-none resize-none text-gray-700 transition-all" rows="2"></textarea>
            </div>
        </div>

        <div className="bg-gray-50 px-8 py-5 border-t border-gray-100 flex justify-end gap-3 shrink-0">
            <button onClick={onClose} className="px-6 py-2.5 rounded-xl text-gray-500 font-bold hover:bg-gray-200 transition-colors text-sm">Cancelar</button>
            <button onClick={handleSave} disabled={loading} className="px-8 py-2.5 rounded-xl bg-sabesp-cyan text-white font-bold hover:bg-cyan-600 transition-all shadow-lg hover:shadow-xl text-sm flex items-center gap-2 disabled:opacity-70">
                {loading ? (<><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>Enviando...</>) : '💾 Salvar Alterações'}
            </button>
        </div>
      </div>
    </div>
  );
}

export default FluxogramaModal;